'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineInfo } from '@/app/components/icons/BaseLineInfo';
import { PostReferenceFilter } from '@/app/components/shared/PostReferenceFilter';
import { SearchInput, type SearchInputRef } from '@/app/components/shared/SearchInput';
import { SettingRadioGroup } from '@/app/components/shared/SettingRadioGroup';
import { Loader } from '@/app/components/ui/loader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { toast } from '@/app/components/ui/use-toast';
import { useExtraTag, useToolContainerOverlayPosition } from '@/app/hooks';
import type { ElementType, SortByType } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { useElementSelector } from '../../hooks/use-element-selector';
import { type ElementsSelectorProps, HEAD_TAB } from '../../types';
import { LabelFilter } from '../LabelFilter';
import { CollectionFilter } from './CollectionFilter';
import { ElementList } from './ElementList';
import { PostReferencePanel } from './PostReferencePanel';

/**
 * Drawer 模式的 Elements Selector (PC 端侧边栏抽屉)
 * 特点：
 * 1. 包含 CollectionFilter (合集筛选)
 * 2. 社区分享使用 DemoVideoView 展示
 * 3. 自动选择模式（选择后自动调用 onSelect）
 */
export default function DrawerSelector(props: ElementsSelectorProps) {
  const {
    isOpen,
    onClose,
    onSelect,
    remainingSelections = 6,
    maxReferenceCount,
    allowedModalities,
    onDelete,
    onCreateOpen,
    pendingDeleteIds,
    setPendingDeleteIds,
    disabledIds,
  } = props;

  const t = useTranslations();

  const { allTag, likeTag, collectTag } = useExtraTag();
  // 使用统一的业务逻辑 Hook
  const selector = useElementSelector(props);

  const [isAnimating, setIsAnimating] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);

  const shareReferenceSearchRef = useRef<SearchInputRef>(null);
  const myReferenceSearchRef = useRef<SearchInputRef>(null);
  const overlayPosition = useToolContainerOverlayPosition({
    open: isOpen,
    viewportPadding: 50,
  });

  useEffect(() => {
    if (isOpen) {
      setIsExpanding(true);
    } else {
      setIsExpanding(false);
    }
  }, [isOpen]);

  // 处理抽屉打开动画
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // 等待抽屉动画完成（通常抽屉动画为 300-500ms）
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // DrawerSelector 特有的选择逻辑：自动调用 onSelect
  // 重写 toggleElementSelection 以支持取消选择时调用 onDelete
  const handleToggleElementSelection = (element: ElementType) => {
    const isCurrentlySelected = disabledIds?.includes(element.id || '');
    // 如果是取消选择，调用 onDelete
    if (isCurrentlySelected) {
      onDelete?.([element.id || '']);
    } else {
      if (remainingSelections <= 0) {
        toast({
          type: 'warning',
          message: allowedModalities?.includes('video')
            ? t('elements.elementDialog.elementOverLimitStrVideo', {
                count: maxReferenceCount || remainingSelections,
              })
            : t('elements.elementDialog.elementOverLimitStrImage', {
                count: maxReferenceCount || remainingSelections,
              }),
        });
        return;
      }
      onSelect([element], false);
    }

    // 调用原始的 toggle 方法
    selector.toggleElementSelection(element);
  };

  useEffect(() => {
    if (pendingDeleteIds?.length) {
      const filteredElements = selector.selectedElements.filter(
        (element) => !pendingDeleteIds.includes(element.id || ''),
      );
      selector.setSelectedElements?.(filteredElements);
      setPendingDeleteIds?.([]);
    }
  }, [pendingDeleteIds]);

  const containerStyle = {
    top: `${overlayPosition.top}px`,
    left: `${overlayPosition.left}px`,
    height: `${overlayPosition.height}px`,
    width: isExpanding ? `${overlayPosition.availableWidth}px` : '0px',
  };
  const allowedModalitiesLabel =
    allowedModalities?.map((modality) => t(`elements.elementDialog.modality.${modality}`)).join(' / ') ?? '';

  if (!isOpen) return null;

  // 动画进行中时显示 loading
  if (isAnimating) {
    return (
      <div
        className={cn(
          'fixed z-20 flex max-w-5xl flex-col overflow-hidden rounded-r-12 border-none bg-[#17191B] text-white',
          'transition-[width,padding] duration-300 ease-out',
          isExpanding ? 'px-6 pt-4 pb-2' : 'p-0',
        )}
        style={containerStyle}
      >
        <div
          className={cn(
            'absolute top-5 right-6 cursor-pointer transition-opacity delay-150 duration-300 ease-out',
            isExpanding ? 'opacity-100' : 'opacity-0',
          )}
          onClick={onClose}
        >
          <BaseLineClose className="size-4" />
        </div>
        <div
          className={cn(
            'flex h-full w-full items-center justify-center transition-opacity delay-150 duration-300 ease-out',
            isExpanding ? 'opacity-100' : 'opacity-0',
          )}
        >
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'fixed z-20 flex max-w-5xl flex-col overflow-hidden rounded-r-12 border-none bg-[#17191B] text-white',
        'transition-[width,padding] duration-300 ease-out',
        isExpanding ? 'px-6 pt-4 pb-2' : 'p-0',
      )}
      style={containerStyle}
    >
      <div
        className={cn(
          'absolute top-5 right-6 cursor-pointer transition-opacity delay-150 duration-300 ease-out',
          isExpanding ? 'opacity-100' : 'opacity-0',
        )}
        onClick={onClose}
      >
        <BaseLineClose className="size-4" />
      </div>

      <div
        className={cn(
          'flex-1 overflow-y-hidden transition-opacity delay-150 duration-300 ease-out',
          isExpanding ? 'opacity-100' : 'opacity-0',
        )}
      >
        <Tabs value={selector.activeHeadTab} className="flex h-full flex-col" onValueChange={selector.onHeadTabChange}>
          <TabsList className="flex h-fit w-fit gap-6 bg-none bg-transparent p-0">
            {selector.headTabs.map((it) => (
              <TabsTrigger
                key={it.key}
                value={it.key}
                className={cn(
                  'relative px-0 py-1 font-normal text-sm text-system-white64 hover:bg-transparent',
                  selector.activeHeadTab === it.key &&
                    'before:-left-[1px] before:-right-[1px] font-medium text-system-white before:absolute before:bottom-0 before:h-[2px] before:rounded-full before:bg-system-white before:content-[""]',
                )}
              >
                {it.text}
              </TabsTrigger>
            ))}
            {allowedModalities?.length && (
              <div className="flex items-center justify-center gap-1 text-xs">
                <BaseLineInfo className="size-3 text-system-white64" />
                {t('elements.elementDialog.showModalities.others', { modalities: allowedModalitiesLabel })}
              </div>
            )}
          </TabsList>

          {/* 我的元素 Tab */}
          {selector.activeHeadTab === HEAD_TAB.REFERENCE && (
            <TabsContent value={HEAD_TAB.REFERENCE} className="flex flex-1 flex-col">
              <div className="flex w-full flex-1 flex-col gap-4 overflow-y-auto rounded-lg text-white">
                <div className="mt-4">
                  <div className="flex justify-end">
                    <SearchInput
                      ref={myReferenceSearchRef}
                      className="w-full"
                      onSearch={selector.handleSetSearchValue}
                      defaultValue={selector.searchValue}
                      placeholder={t('shareReference.search.placeholder.explore')}
                      inputClassName="focus-visible:bg-system-white10 pe-9 border-system-white08 h-9 text-xs bg-system-white04"
                      maxLength={60}
                    />
                  </div>

                  <LabelFilter
                    className="mt-2 transition-all duration-300"
                    tags={selector.tags || []}
                    tagSelections={selector.selectedTags}
                    handleTagSelectorChange={selector.handleTagSelectorChange}
                    onDelete={selector.deleteTag}
                  />
                </div>

                <ElementList
                  selectedElements={selector.selectedElements}
                  tagSelectors={selector.selectedTags}
                  toggleElementSelection={handleToggleElementSelection}
                  className={cn('flex-1 overflow-hidden')}
                  disabledIds={selector.disabledIds}
                  tags={selector.tags}
                  elements={selector.elements}
                  isLoading={selector.elementsListReq.loading}
                  hasNextPage={!!selector.elementsListReq.data?.data?.next_page_token}
                  onLoadMore={selector.elementsListReq.run}
                  enableCreate={!selector.searchValue}
                  onCreateOpen={onCreateOpen}
                  onDelete={onDelete}
                />
              </div>
            </TabsContent>
          )}

          {/* 社区分享 Tab */}
          {selector.activeHeadTab === HEAD_TAB.SHARE_REFERENCE && (
            <TabsContent value={HEAD_TAB.SHARE_REFERENCE} className="flex-1 overflow-hidden">
              <div className="hide-scrollbar flex h-full flex-col overflow-y-scroll rounded-lg text-white">
                <div className="mt-4 flex justify-between">
                  <SearchInput
                    ref={shareReferenceSearchRef}
                    className="w-full"
                    defaultValue={selector.searchValue}
                    onSearch={selector.handleSetSearchValue}
                    placeholder={t('shareReference.search.placeholder.explore')}
                    inputClassName="focus-visible:bg-system-white10 pe-9 border-system-white08 h-9 text-xs bg-system-white04"
                    maxLength={60}
                  />
                </div>

                {selector.categories?.length && selector.categories?.length > 0 && (
                  <PostReferenceFilter
                    categories={selector.categories}
                    activeCategory={selector.activeCategory}
                    setActiveCategory={(category) => {
                      if (category && category !== allTag.id && selector.activeCollection) {
                        selector.setActiveCollection(null);
                      }
                      if (category && [likeTag.id, collectTag.id].includes(category)) {
                        myReferenceSearchRef.current?.setValue('');
                        shareReferenceSearchRef.current?.setValue('');
                      }
                      selector.setActiveCategory(category);
                    }}
                    className="mt-3 mb-4"
                    size="small"
                  />
                )}

                {selector.collections.length > 0 && !selector.searchValue && selector.activeCategory === allTag.id && (
                  <div className="mb-2 font-medium text-base leading-[1.4]">
                    {t('elements.elementDialog.collection')}
                  </div>
                )}
                {selector.collections.length > 0 && !selector.searchValue && selector.activeCategory === allTag.id && (
                  <CollectionFilter
                    collections={selector.collections}
                    setActiveCollection={(it) => {
                      selector.setActiveCollection(it);
                      const searchValue = it.display_name || '';
                      shareReferenceSearchRef.current?.setValue(searchValue);
                    }}
                  />
                )}

                <div className="mt-4 flex justify-between">
                  <div className="font-medium text-base leading-[1.4]">{t('elements.elementDialog.community')}</div>
                  <SettingRadioGroup
                    options={selector.sortOptions}
                    className="mb-4 w-fit rounded-8 bg-system-white04"
                    activeOptionClassName="border-none bg-system-white12"
                    value={selector.sortBy}
                    onValueChange={(val) => {
                      reportEvent('reference_sort_click', {
                        name: (val as SortByType) === 'recommend' ? 'recommend' : 'new',
                      });
                      selector.setSortBy(val as SortByType);
                    }}
                    labelClassName="font-normal text-system-white48"
                    optionsWrapperClassName="h-6 text-system-white text-xs font-normal"
                  />
                </div>

                <div className="flex-1 overflow-y-auto" id="element-selector-scroll">
                  <PostReferencePanel
                    toggleElementSelection={handleToggleElementSelection}
                    onExpose={selector.onExpose}
                    deeps={[selector.sortBy, selector.activeCategory]}
                    disabledIds={selector.disabledIds}
                    elements={selector.elements}
                    isLoading={selector.postReferenceListReq.loading}
                    hasNextPage={!!selector.postReferenceListReq.data?.data?.next_page_token}
                    onLoadMore={selector.getShareReferenceData}
                    onDelete={onDelete}
                  />
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
